/**
 * Modo garçom (0085, F1c): pedidos da mesa ENVIA­DOS pelo cliente, agrupados
 * por mesa — o garçom identifica quem pediu o quê (nome do cliente), com os
 * extras e os ingredientes removidos de cada item, e avança o pedido até
 * "lançado no sistema local" do restaurante (POS). Sem kanban: o pedido
 * termina aqui.
 *
 * Os nomes dos itens vêm no idioma do painel (cascata do cardápio — a ponte
 * de tradução cliente ↔ restaurante).
 */
import { useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ordersApi, tables as tablesApi } from '../lib/api.js'
import { getTokenPayload } from '../lib/auth.js'
import type { OrderDetailed } from '../lib/api.js'
import { useI18n } from '../lib/i18n.js'
import { Button, Empty, Spinner, toast } from './ui/index.js'
import { cn } from '../lib/utils.js'

type FlowStatus = 'waiter_sent' | 'waiter_ack' | 'relayed'

const FLOW_STATUSES: FlowStatus[] = ['waiter_sent', 'waiter_ack', 'relayed']

function isFlowStatus(s: OrderDetailed['status']): s is FlowStatus {
  return (FLOW_STATUSES as string[]).includes(s)
}

function elapsedLabel(createdAt: number): string {
  const sec = Math.max(0, Math.floor(Date.now() / 1000) - createdAt)
  const min = Math.floor(sec / 60)
  return min >= 60 ? `${Math.floor(min / 60)}h${min % 60}` : `${min} min`
}

export function WaiterTables({
  lang,
  priceLocale = '',
  currency = 'BRL',
  mineOnly = false,
}: {
  lang: string
  /** #428: locale de formatação Intl (língua base + país do restaurante) */
  priceLocale?: string
  currency?: string
  /** #132: mostrar só os grupos das mesas que o garçom declarou atender */
  mineOnly?: boolean
}) {
  const queryClient = useQueryClient()
  const { t } = useI18n()

  // #132: claims do garçom logado — filtra os grupos de mesa quando ativo
  const me = getTokenPayload()?.sub ?? ''
  const { data: claims = [] } = useQuery({
    queryKey: ['table-claims'],
    queryFn: tablesApi.listClaims,
  })
  const myTableIds = useMemo(
    () => new Set(claims.filter((cl) => cl.waiter_user_id === me).map((cl) => cl.table_id)),
    [claims, me],
  )

  const { data: orders, isLoading } = useQuery({
    queryKey: ['orders', 'waiter-flow', lang],
    queryFn: () => ordersApi.list({ status: FLOW_STATUSES.join(','), limit: 100, locale: lang }),
    refetchInterval: 15_000,
  })

  const advance = useMutation({
    mutationFn: ({ id, status }: { id: string; status: FlowStatus }) =>
      ordersApi.updateStatus(id, status),
    onSuccess: (_d, v) => {
      void queryClient.invalidateQueries({ queryKey: ['orders', 'waiter-flow'] })
      void queryClient.invalidateQueries({ queryKey: ['orders'] })
      toast.success(
        v.status === 'waiter_ack' ? t('admin.waiter.flow_ack') : t('admin.waiter.flow_relayed'),
      )
    },
    onError: (e: Error) => toast.error(e.message),
  })

  // agrupa por MESA; pedidos de retirada/entrega (sem mesa) agrupam por
  // canal — no fluxo garçom todos os canais terminam no mesmo painel
  const groups = useMemo(() => {
    const flow = (orders ?? []).filter((o) => isFlowStatus(o.status))
    const byTable = new Map<string, OrderDetailed[]>()
    for (const o of flow) {
      const key = o.table_id ?? `_canal-${o.channel}`
      const list = byTable.get(key) ?? []
      list.push(o)
      byTable.set(key, list)
    }
    for (const list of byTable.values()) {
      list.sort((a, b) => {
        const rank = (s: OrderDetailed['status']) => FLOW_STATUSES.indexOf(s as FlowStatus)
        return rank(a.status) - rank(b.status) || a.created_at - b.created_at
      })
    }
    // mesas com pedidos pendentes primeiro, depois por número da mesa
    const entries = [...byTable.entries()].sort((a, b) => {
      const pending = (l: OrderDetailed[]) => (l.some((o) => o.status !== 'relayed') ? 0 : 1)
      return pending(a[1]) - pending(b[1])
    })
    // "só as minhas" (#132): grupos de mesa fora das claims saem; retirada/
    // entrega (sem mesa) permanece — são do salão como um todo
    if (mineOnly && myTableIds.size > 0) {
      return entries.filter(([key]) => key.startsWith('_canal-') || myTableIds.has(key))
    }
    return entries
  }, [orders, mineOnly, myTableIds])

  const tableLabel = (order: OrderDetailed): string => {
    if (order.table_id === null || order.table_id === undefined) {
      // sem mesa = retirada/entrega no fluxo garçom
      return order.channel === 'takeaway'
        ? t('admin.channel.takeaway')
        : order.channel === 'delivery'
          ? t('admin.channel.delivery')
          : '?'
    }
    const num = order.table_number
    const label = order.table_label
    if (num === null || num === undefined) return label ?? '?'
    return label ? `#${num} · ${label}` : `#${num}`
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-40">
        <Spinner className="w-8 h-8" />
      </div>
    )
  }

  if (groups.length === 0) {
    return <Empty title={t('admin.waiter.flow_empty')} />
  }

  return (
    <div
      className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 items-start"
      data-testid="waiter-flow-tables"
    >
      {groups.map(([tableKey, tableOrders]) => (
        <div
          key={tableKey}
          data-testid={`waiter-flow-table-${tableKey}`}
          className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden"
        >
          <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
            <div>
              <p className="text-sm font-bold text-gray-900">{tableLabel(tableOrders[0]!)}</p>
              <p className="text-xs text-gray-500">
                {tableOrders.length} pedido{tableOrders.length > 1 ? 's' : ''}
              </p>
            </div>
            <span
              className={cn(
                'text-[11px] font-bold px-2 py-1 rounded-full',
                tableOrders.some((o) => o.status !== 'relayed')
                  ? 'bg-indigo-100 text-indigo-700'
                  : 'bg-gray-100 text-gray-500',
              )}
            >
              {tableOrders.some((o) => o.status !== 'relayed')
                ? t('admin.waiter.flow_sent')
                : t('admin.waiter.flow_relayed')}
            </span>
          </div>
          <div className="divide-y divide-gray-100">
            {tableOrders.map((order) => (
              <FlowOrderCard
                key={order.id}
                order={order}
                advance={advance}
                currency={currency}
                priceLocale={priceLocale}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

function FlowOrderCard({
  order,
  advance,
  currency,
  priceLocale = '',
}: {
  order: OrderDetailed
  advance: { mutate: (v: { id: string; status: FlowStatus }) => void; isPending: boolean }
  currency: string
  /** #428: locale de formatação Intl (língua base + país do restaurante) */
  priceLocale?: string
}) {
  const { t, lang } = useI18n()
  const customer = order.reference_name ?? order.customer_name ?? '?'

  const statusChip =
    order.status === 'waiter_sent'
      ? t('admin.waiter.flow_sent')
      : order.status === 'waiter_ack'
        ? t('admin.waiter.flow_ack')
        : t('admin.waiter.flow_relayed')

  return (
    <div data-testid={`waiter-flow-order-${order.id}`} className="p-4 space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-gray-900" data-testid="waiter-flow-customer">
            {customer}
          </p>
          <p className="text-xs text-gray-400">{elapsedLabel(order.created_at)}</p>
        </div>
        <span className="text-[11px] font-bold px-2 py-1 rounded-full bg-gray-100 text-gray-600">
          {statusChip}
        </span>
      </div>

      <ul className="space-y-1.5">
        {order.items.map((item) => (
          <li key={item.id} className="text-sm text-gray-800">
            <span className="font-bold">{item.quantity}×</span> {item.product_name}
            {item.variant_label ? (
              <span className="text-gray-500"> · {item.variant_label}</span>
            ) : null}
            {item.extras.length > 0 && (
              <div className="pl-4 text-xs text-gray-500">
                {item.extras.map((e) => (
                  <div key={e.extra_id}>
                    + {e.name}
                    {e.quantity > 1 ? ` (${e.quantity}×)` : ''}
                  </div>
                ))}
              </div>
            )}
            {item.removed_ingredients.length > 0 && (
              <div className="pl-4 text-xs text-amber-700">
                {item.removed_ingredients.map((name) => (
                  <div key={name}>− {name}</div>
                ))}
              </div>
            )}
          </li>
        ))}
      </ul>

      {order.notes ? (
        <p className="text-xs text-gray-600 bg-amber-50 border border-amber-100 rounded px-2 py-1.5">
          “{order.notes}”
        </p>
      ) : null}

      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-bold text-gray-900">
          {new Intl.NumberFormat(priceLocale || lang || 'pt', {
            style: 'currency',
            currency,
          }).format(order.total_cents / 100)}
        </span>
        <div className="flex gap-2">
          {order.status === 'waiter_sent' && (
            <Button
              size="sm"
              data-testid={`waiter-flow-ack-${order.id}`}
              disabled={advance.isPending}
              onClick={() => advance.mutate({ id: order.id, status: 'waiter_ack' })}
            >
              {t('admin.waiter.flow_ack_action')}
            </Button>
          )}
          {order.status === 'waiter_ack' && (
            <Button
              size="sm"
              data-testid={`waiter-flow-relay-${order.id}`}
              disabled={advance.isPending}
              onClick={() => advance.mutate({ id: order.id, status: 'relayed' })}
            >
              {t('admin.waiter.flow_relay_action')}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
